import React, { useMemo, useState, useCallback, useRef, useLayoutEffect } from 'react';
import { FaGithub } from 'react-icons/fa6';

const ITEMS = [

  {
    title: 'RentAI',
    blurb: 'Developed RentAI, an agentic rental assistant built with React/Next.js and FastAPI to streamline housing search, tracking, and automated landlord communication; earned MHacks Fetch.AI Honorable Mention (4th). Built a Pandas ETL pipeline to preprocess 1,000+ listings for fast semantic search, and implemented Google Gemini–based NLP for intent parsing and recommendations. Designed MCP-style tool-calling workflows integrating Fetch.ai agents, AgentMail APIs, and webhooks to automate and manage landlord email conversations.',
    link: 'https://github.com/AhaanSh/RentAI',
    tech: ['React', 'Next.js', 'FastAPI', 'Gemini', 'Fetch.ai'],
    details: 'Developed RentAI, an agentic rental assistant built with React/Next.js and FastAPI to help users find housing and sign leases through streamlined search, tracking, and automated landlord communication; earned MHacks Fetch.AI Honorable Mention (4th). Built an ETL pipeline using Pandas to ingest and preprocess 1,000+ rental listings from the RentCast API by flattening nested JSON and standardizing fields for fast semantic search and filtering. Implemented Google Gemini–powered NLP for intent parsing and context-aware recommendations with real-time filtering and ranking. Designed autonomous tool-calling MCP workflows integrating Fetch.AI agents, AgentMail APIs, and webhook handlers to manage state, parse requests, and automate landlord email chains with synchronized chat and email interactions.',
    suit: '♥',
    rank: 'A',
  },

  {
    title: 'ALF',
    blurb: 'OCaml interpreter for a Simply Typed Lambda Calculus with integers, booleans, let-bindings, and functions. Built with Menhir and OCamllex in a modular architecture for maintainability and extension.',
    link: 'https://github.com/P4ttyCakes/Alf',
    tech: ['OCaml', 'Menhir', 'OCamllex'],
    details: 'Built an AST and recursive evaluator to type-check and execute programs. Implemented a Menhir parser and OCamllex lexer for efficient syntax analysis. Developed a modular pipeline for lexing, parsing, type checking and execution with clear error messages. Packaged with Dune for reproducible builds and fast CLI execution (e.g., echo "let x = 10 in (fun y -> y * 2) (x + 5)" | dune exec ./main.exe → 30).',
    suit: '♠',
    rank: 'K',
  },
  
  {
    title: 'DeepStackR1',
    blurb: 'Automated heads-up Texas Hold’em bot with live game parsing and LLM-powered strategy, built in Python with Selenium and DeepSeek-Chat.',
    link: 'https://github.com/P4ttyCakes/DeepStackR1',
    tech: ['Python', 'Selenium', 'OpenAI API'],
    details:
      'Developed a fully automated poker bot that interfaces with PokerNow via Selenium WebDriver, using custom CSS selectors to read table state and execute calls, raises, and folds. Integrated DeepSeek-Chat through the OpenAI API to dynamically generate strategic decisions based on hand history, board texture, and betting patterns. Designed for adaptive play, enabling rapid iteration on strategy and move selection in live games.',
    suit: '♦',
    rank: 'J',
  },
  
  {
    title: 'C++ Text Classifier',
    blurb: 'High-performance C++17 forum post classification engine using Naive Bayes. Scaled to 2,552 posts and a 6,267-token vocabulary with 95.5% accuracy over 3,300+ tests.',
    link: 'https://github.com/P4ttyCakes/High-Performance-C-Text-Classification-Engine-',
    tech: ['C++', 'CLI', 'CSV Parsing'],
    details:
      'Engineered a high-performance C++17 forum post classification system that trains on labeled CSV data and predicts tags for unseen posts using a Naive Bayes probabilistic model; scaled to 2,552 posts and a 6,267-token vocabulary. Increased accuracy from 85.7% to 95.5% over 3,300+ tests by optimizing Multinomial Naive Bayes with Laplace smoothing and token normalization; preserved deterministic outputs and passed full regression tests.',
    suit: '♦',
    rank: '9',
  },

  {
    title: 'Portfolio Website',
    blurb: 'React portfolio with functional components, custom hooks, and CSS animations. Features interactive poker card UI and responsive design.',
    link: 'https://github.com/P4ttyCakes/portfolio',
    tech: ['React', 'JavaScript', 'Bootstrap'],
    details:
      'Built React portfolio using functional components with useState, useCallback, and useMemo hooks. Implemented custom useIntersectionObserver hook for scroll animations and CSS keyframe animations for card runouts. Created reusable component architecture with proper event handling and responsive layouts using CSS Grid/Flexbox.',
    suit: '♠',
    rank: '8',
  }

  
];

const ProjectsLite = React.memo(() => {
  const [active, setActive] = useState(null);
  const items = useMemo(() => ITEMS, []);
  const open = useCallback((p) => setActive(p), []);
  const close = useCallback(() => setActive(null), []);

  // Hide the active card from the hand (ghost), recompute fan mid for visible cards
  const visibleItems = useMemo(
    () => (active ? items.filter((it) => it.title !== active.title) : items),
    [items, active]
  );
  const midIndex = useMemo(
    () => (visibleItems.length - 1) / 2,
    [visibleItems.length]
  );

  // Precompute parabolic translateY per card: y = a*(offset^2) + b
  const handStyleVars = useMemo(() => {
    const a = 12; // curvature factor in px
    const b = 24; // base lift so center sits lowest
    return visibleItems.map((_, i) => {
      const offset = i - midIndex;
      const translateYPx = a * (offset * offset) + b;
      const rotateDeg = 4 * offset;
      return {
        '--i': i,
        '--mid': midIndex,
        '--ty': `${translateYPx}px`,
        '--rot': `${rotateDeg}deg`,
      };
    });
  }, [visibleItems, midIndex]);

  return (
    <div className="projects-lite">
      <div className="proj-table">
        <div className="proj-table-felt">
          {active ? (
            <div className="proj-card proj-card-expanded" role="region" aria-label={`${active.title} details`}>
              <span className="corner top-left">
                <span className="rank">{active.rank}</span>
                <span className={`suit ${active.suit === '♥' || active.suit === '♦' ? 'red' : ''}`}>{active.suit}</span>
              </span>
              <span className="corner bottom-right">
                <span className="rank">{active.rank}</span>
                <span className={`suit ${active.suit === '♥' || active.suit === '♦' ? 'red' : ''}`}>{active.suit}</span>
              </span>
              {active.link && (
                <a 
                  href={active.link} 
                  target="_blank" 
                  rel="noreferrer" 
                  className="proj-card-github-link proj-card-github-link-expanded" 
                  aria-label={`View ${active.title} on GitHub`}
                >
                  <FaGithub className="proj-card-github-icon" />
                </a>
              )}
              <button className="proj-card-close" onClick={close} aria-label="Close">×</button>
              <div className="proj-card-body">
                <div className="projects-lite-title"><span>{active.title}</span></div>
                <div className="projects-lite-blurb">{active.details}</div>
                {active.tech?.length ? (
                  <AutoFitTags tags={active.tech} />
                ) : null}
              </div>
            </div>
          ) : (
            <div className="proj-detail-hint">Select a card to see project details</div>
          )}
        </div>
        <div className="proj-hand fan">
          {visibleItems.map((p, i) => (
            <button
              key={p.title}
              type="button"
              className="proj-card proj-card-long"
              onClick={() => open(p)}
              aria-label={`Open ${p.title}`}
              style={handStyleVars[i]}
            >
              <span className="corner top-left">
                <span className="rank">{p.rank}</span>
                <span className={`suit ${p.suit === '♥' || p.suit === '♦' ? 'red' : ''}`}>{p.suit}</span>
              </span>
              <span className="corner bottom-right">
                <span className="rank">{p.rank}</span>
                <span className={`suit ${p.suit === '♥' || p.suit === '♦' ? 'red' : ''}`}>{p.suit}</span>
              </span>
              {p.link && (
                <a 
                  href={p.link} 
                  target="_blank" 
                  rel="noreferrer" 
                  className="proj-card-github-link" 
                  onClick={(e) => e.stopPropagation()}
                  aria-label={`View ${p.title} on GitHub`}
                >
                  <FaGithub className="proj-card-github-icon" />
                </a>
              )}
              <div className="proj-card-body">
                <div className="projects-lite-title"><span>{p.title}</span></div>
                <div className="projects-lite-blurb">{p.blurb}</div>
                {p.tech?.length ? (
                  <AutoFitTags tags={p.tech} />
                ) : null}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* modal removed; details shown in-felt above */}
    </div>
  );
});

export default ProjectsLite;

// Auto-fit tags into one line by scaling if needed
const AutoFitTags = ({ tags }) => {
  const wrapRef = useRef(null);
  const innerRef = useRef(null);
  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    const inner = innerRef.current;
    if (!wrap || !inner) return;
    // reset scale
    wrap.style.setProperty('--tags-scale', '1');
    const wrapWidth = wrap.clientWidth;
    const innerWidth = inner.scrollWidth;
    if (innerWidth > wrapWidth && wrapWidth > 0) {
      const scale = Math.max(0.6, wrapWidth / innerWidth);
      wrap.style.setProperty('--tags-scale', String(scale));
    }
  });
  return (
    <div ref={wrapRef} className="projects-lite-tags">
      <div ref={innerRef} style={{ display: 'inline-flex', gap: 6 }}>
        {tags.map((t) => (
          <span key={t} className="projects-lite-tag">{t}</span>
        ))}
      </div>
    </div>
  );
};



